import React, { useState } from "react";

import { X } from "lucide-react";
import { ErrorCode, useDropzone } from "react-dropzone";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CheckboxField } from "@/src/components/form-fields/CheckboxField";
import { Button } from "@/src/components/ui/button";
import { FieldError } from "@/src/components/ui/field";

import type { JSX } from "react";
import type { FileRejection } from "react-dropzone";

import type { AddFontFormValues } from "./addFontSchema";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

const FILE_INVALID_TYPE_CODE: string = ErrorCode.FileInvalidType;
const FILE_TOO_LARGE_CODE: string = ErrorCode.FileTooLarge;

const ACCEPTED_FONT_TYPES = {
  "font/woff2": [".woff2"],
  "font/woff": [".woff"],
  "font/ttf": [".ttf"],
  "font/otf": [".otf"],
};

interface DropError {
  translationKey: "fileTooLarge" | "invalidFileType" | "duplicateFileName";
  name: string;
}

function findRejectionByCode(
  fileRejectionList: FileRejection[],
  code: string,
): FileRejection | undefined {
  return fileRejectionList.find((rejection) =>
    rejection.errors.some((error) => error.code === code),
  );
}

function buildDropError(
  fileRejectionList: FileRejection[],
  duplicateFile: File | undefined,
): DropError | null {
  const invalidTypeRejection = findRejectionByCode(
    fileRejectionList,
    FILE_INVALID_TYPE_CODE,
  );
  if (invalidTypeRejection) {
    return {
      translationKey: "invalidFileType",
      name: invalidTypeRejection.file.name,
    };
  }

  const tooHeavyRejection = findRejectionByCode(
    fileRejectionList,
    FILE_TOO_LARGE_CODE,
  );
  if (tooHeavyRejection) {
    return {
      translationKey: "fileTooLarge",
      name: tooHeavyRejection.file.name,
    };
  }

  if (duplicateFile) {
    return { translationKey: "duplicateFileName", name: duplicateFile.name };
  }

  return null;
}

function AddFontForm(): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const { control } = useFormContext<AddFontFormValues>();
  const { field: fileListField } = useController({
    control,
    name: "fileList",
  });
  const fileList = fileListField.value;

  const [dropError, setDropError] = useState<DropError | null>(null);

  const handleDrop = (
    acceptedFileList: File[],
    fileRejectionList: FileRejection[],
  ): void => {
    const existingNameList = fileList.map((file) => file.name);
    const newFileList = acceptedFileList.filter(
      (file, index) =>
        !existingNameList.includes(file.name) &&
        acceptedFileList.findIndex((other) => other.name === file.name) ===
          index,
    );
    const duplicateFile = acceptedFileList.find(
      (file) => !newFileList.includes(file),
    );

    setDropError(buildDropError(fileRejectionList, duplicateFile));
    fileListField.onChange([...fileList, ...newFileList]);
  };

  const { getInputProps, getRootProps } = useDropzone({
    accept: ACCEPTED_FONT_TYPES,
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop: handleDrop,
  });

  const handleRemove = (name: string): void => {
    fileListField.onChange(fileList.filter((file) => file.name !== name));
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getRootProps()}
        className="border-border text-muted-foreground cursor-pointer rounded-lg border border-dashed p-6 text-center text-sm"
      >
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} data-testid="font-dropzone-input" />
        <p>{t("dropzoneLabel")}</p>
        <p className="text-xs">{t("dropzoneHint")}</p>
      </div>
      {dropError ? (
        <FieldError>
          {t(dropError.translationKey, { name: dropError.name })}
        </FieldError>
      ) : null}
      {fileList.map((file) => (
        <div
          key={file.name}
          className="flex items-center justify-between text-sm"
        >
          <span>{file.name}</span>
          <Button
            aria-label={t("removeFile")}
            onClick={(): void => handleRemove(file.name)}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
      ))}
      <CheckboxField<AddFontFormValues>
        label={t("licenseDeclaration")}
        name="isLicenseDeclared"
      />
    </div>
  );
}

export default AddFontForm;
